import { eventChannel } from 'redux-saga';
import { all, call, put, select, take, takeEvery, takeLatest, delay, fork } from 'redux-saga/effects';

import axios from '../../axiosInstance';
import requests from '../../requests';
import { i18n } from '../../i18n';

import { authActionTypes } from '../auth/auth.types';
import { workspaceActionTypes } from '../workspace/workspace.types';
import { showNotify } from '../notify/notify.actions';
import { updateSessionStatus } from '../auth/auth.actions';

import { preferencesActionTypes } from './preferences.types';
import { PREF_FIELDS } from './preferences.fields';
import {
    preferencesFetchRequest,
    preferencesFetchSuccess,
    preferencesFetchFailure,
    preferencesPatchSuccess,
    preferencesPatchFailure,
    preferencesPatchRevert,
    preferencesReset,
    systemThemeChanged,
} from './preferences.actions';

const THEME_CACHE_KEY = 'theme_cache';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function writeThemeCache(resolvedTheme) {
    try {
        if (resolvedTheme === 'light' || resolvedTheme === 'dark') {
            localStorage.setItem(THEME_CACHE_KEY, resolvedTheme);
        }
    } catch (_) { }
}

function clearThemeCache() {
    try {
        localStorage.removeItem(THEME_CACHE_KEY);
    } catch (_) { }
}

function applyHtmlThemeClass(resolvedTheme) {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    html.classList.remove('light-mode', 'dark-mode');
    if (resolvedTheme === 'light') html.classList.add('light-mode');
    else if (resolvedTheme === 'dark') html.classList.add('dark-mode');
}

function resolveTheme(theme, systemIsDark) {
    if (theme === 'system') return systemIsDark ? 'dark' : 'light';
    return theme;
}

// Maps an axios error from the prefs endpoints to a UX response.
// Yields actions; never throws.
function* handlePrefsError(err, revertPrev) {
    const status = err?.response?.status;
    const body = err?.response?.data;
    if (status === 400) {
        // const msg = body?.details?.[0]?.message || body?.message || 'Invalid value';
        yield put(showNotify({ message: 'couldnt_save' }));
        if (revertPrev) yield put(preferencesPatchRevert(revertPrev));
    } else if (status === 401) {
        yield put(updateSessionStatus('not_authorized'));
    } else {
        yield put(showNotify({ message: 'try_again' }));
        if (revertPrev) yield put(preferencesPatchRevert(revertPrev));
    }
}

// ---------------------------------------------------------------------------
// FETCH
// ---------------------------------------------------------------------------

export function* fetchPreferences() {
    // Preferences are workspace-scoped (per user+workspace). Without a current
    // workspace there is nothing to fetch — a brand-new pre-onboarding user
    // would get 409 anyway — so initialState defaults apply until a workspace
    // init/switch triggers this saga again. The slice must still be marked
    // initialized here (an empty FETCH_SUCCESS applies nothing but flips
    // isInitialized): App.js gates its whole render on
    // preferencesIsInitialized, so a silent return would blank-screen the
    // zero-workspace onboarding page forever. Creating the first workspace
    // dispatches WORKSPACE_INIT/SWITCH_SUCCESS, which re-runs this saga with
    // a real id.
    const workspaceId = yield select((s) => s.workspace?.currentWorkspace?.id);
    if (!workspaceId) {
        yield put(preferencesFetchSuccess(null));
        return;
    }

    yield put(preferencesFetchRequest());
    try {
        const { data } = yield axios.get(requests.userPreferences(workspaceId));
        const prefs = data?.preferences;
        yield put(preferencesFetchSuccess(prefs));

        // Language follows the account: apply the stored language if the
        // backend has one and it differs from the active i18next language.
        if (prefs?.language && prefs.language !== i18n.language) {
            i18n.changeLanguage(prefs.language);
        }

        // Apply theme to <html> and update cache.
        const systemIsDark = yield select((s) => s.preferences.systemIsDark);
        const resolved = resolveTheme(prefs.theme, systemIsDark);
        applyHtmlThemeClass(resolved);
        writeThemeCache(resolved);
    } catch (e) {
        yield put(preferencesFetchFailure(e));
        // Do not block the app — initialState defaults are already applied.
    }
}

// ---------------------------------------------------------------------------
// PATCH
//
// The reducer applies the optimistic update on PREFERENCES_PATCH_REQUEST.
// Saga sends the network call; on success it replaces with server blob;
// on failure it reverts to the snapshot we capture before the request.
// ---------------------------------------------------------------------------

function* patchPreferencesNow(partial) {
    // Workspace-scoped endpoint — no current workspace means nothing to persist
    // (the preference UIs are unreachable in that state anyway).
    const workspaceId = yield select((s) => s.workspace?.currentWorkspace?.id);
    if (!workspaceId) return;

    // Snapshot the slice values that are about to change so we can revert.
    const prev = yield select((s) => {
        const snap = {};
        PREF_FIELDS.forEach(({ wire, store }) => {
            if (wire in partial) snap[store] = s.preferences[store];
        });
        // Diagram patches are partial merges — snapshot the whole object.
        if ('diagram' in partial) snap.diagram = s.preferences.diagram;
        return snap;
    });

    try {
        const { data } = yield axios.patch(requests.userPreferences(workspaceId), partial);
        const prefs = data?.preferences;
        yield put(preferencesPatchSuccess(prefs));

        // If theme was part of the patch, refresh <html> class and cache.
        if ('theme' in partial) {
            const systemIsDark = yield select((s) => s.preferences.systemIsDark);
            const resolved = resolveTheme(prefs.theme, systemIsDark);
            applyHtmlThemeClass(resolved);
            writeThemeCache(resolved);
        }
    } catch (e) {
        yield put(preferencesPatchFailure(e));
        yield* handlePrefsError(e, prev);
    }
}

// Sidebar-only patches are debounced (reduces traffic on rapid toggles).
// Other patches are sent immediately for responsive feedback.
// `takeLatest` cancels any pending patch when a new request arrives, so the
// debounce window resets on every click.
function* watchPatchRequests() {
    yield takeLatest(preferencesActionTypes.PREFERENCES_PATCH_REQUEST, function* (action) {
        const partial = action.payload || {};
        const keys = Object.keys(partial);
        const isSidebarOnly = keys.length === 1 && keys[0] === 'sidebar_collapsed';

        if (isSidebarOnly) {
            yield delay(500);
        }

        yield call(patchPreferencesNow, partial);
    });
}

// ---------------------------------------------------------------------------
// SYSTEM THEME (matchMedia)
//
// One-time startup. `eventChannel` bridges the MediaQueryList change event
// into a saga-friendly stream. The channel is never closed — it lives for
// the app's lifetime.
// ---------------------------------------------------------------------------

function createSystemThemeChannel() {
    return eventChannel((emit) => {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return () => { };
        }
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        emit(mql.matches);
        const handler = (e) => emit(e.matches);
        // addEventListener is the modern API; older Safari falls back via addListener.
        if (mql.addEventListener) mql.addEventListener('change', handler);
        else mql.addListener(handler);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', handler);
            else mql.removeListener(handler);
        };
    });
}

function* watchSystemTheme() {
    const channel = yield call(createSystemThemeChannel);
    while (true) {
        const systemIsDark = yield take(channel);
        yield put(systemThemeChanged(systemIsDark));
        // Re-apply theme if user picked 'system'.
        const theme = yield select((s) => s.preferences.theme);
        if (theme === 'system') {
            const resolved = systemIsDark ? 'dark' : 'light';
            applyHtmlThemeClass(resolved);
            writeThemeCache(resolved);
        }
    }
}

// ---------------------------------------------------------------------------
// SIGN OUT — clear cache, reset slice
// ---------------------------------------------------------------------------

function* onSignOutSuccess() {
    yield takeEvery(authActionTypes.SIGN_OUT_SUCCESS, function* () {
        clearThemeCache();
        yield put(preferencesReset());
    });
}

// ---------------------------------------------------------------------------
// WORKSPACE READY / SWITCH — (re)fetch preferences
//
// Preferences are stored per (user, workspace): they load once the current
// workspace is known (workspace init covers both boot-with-session and
// sign-in) and MUST be refetched on every workspace switch — the previous
// workspace's blob does not apply to the new one.
// ---------------------------------------------------------------------------

function* onWorkspaceReady() {
    yield takeLatest(
        [
            workspaceActionTypes.WORKSPACE_INIT_SUCCESS,
            workspaceActionTypes.WORKSPACE_SWITCH_SUCCESS,
        ],
        fetchPreferences,
    );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export function* preferencesSagas() {
    yield all([
        fork(watchSystemTheme),
        call(onWorkspaceReady),
        call(watchPatchRequests),
        call(onSignOutSuccess),
    ]);
}
